import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { Event, League } from "@liga/shared";
import { api, type AdminEventPatchBody } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";

export function EventsListPanel() {
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);

  const eventsQuery = useQuery({
    queryKey: ["events"],
    queryFn: () => api.events(),
  });

  const leaguesQuery = useQuery({
    queryKey: ["leagues", "all"],
    queryFn: () => api.leagues(),
  });

  const leagueById = useMemo(() => {
    const map = new Map<number, string>();
    for (const l of leaguesQuery.data ?? []) map.set(l.id, l.name);
    return map;
  }, [leaguesQuery.data]);

  const filtered = useMemo(() => {
    const all = eventsQuery.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter((e) => {
      const league = e.leagueId ? leagueById.get(e.leagueId) ?? "" : "";
      return (
        e.name.toLowerCase().includes(term) ||
        e.date.toLowerCase().includes(term) ||
        league.toLowerCase().includes(term) ||
        (e.format ?? "").toLowerCase().includes(term) ||
        (e.meleeEventId ?? "").toLowerCase().includes(term)
      );
    });
  }, [eventsQuery.data, leagueById, search]);

  const editingEvent = useMemo(
    () =>
      editingId !== null
        ? (eventsQuery.data ?? []).find((e) => e.id === editingId) ?? null
        : null,
    [eventsQuery.data, editingId],
  );

  return (
    <section className="rounded-2xl border border-line bg-white p-6 space-y-4">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Eventos
          </div>
          <h2 className="font-display text-xl font-bold text-ink">
            Todos os eventos
          </h2>
          <p className="text-[14px] leading-relaxed text-[#6b6356] mt-1">
            Clique em uma linha para ver os detalhes do evento, ou em
            “Editar” para alterar metadados (incluindo o Melee ID).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, data, liga, Melee ID..."
            className="w-72 max-w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </header>

      {eventsQuery.isLoading || leaguesQuery.isLoading ? (
        <Loading label="Carregando eventos..." />
      ) : eventsQuery.error ? (
        <ErrorBox error={eventsQuery.error} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 py-2 w-32 font-bold">Data</th>
                <th className="text-left px-3 py-2 font-bold">Evento</th>
                <th className="text-left px-3 py-2 font-bold">Liga</th>
                <th className="text-left px-3 py-2 w-28 font-bold">Formato</th>
                <th className="text-left px-3 py-2 w-32 font-bold">Melee</th>
                <th className="text-right px-3 py-2 w-16 font-bold">ID</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((event) => {
                const leagueName = event.leagueId
                  ? leagueById.get(event.leagueId) ?? `#${event.leagueId}`
                  : null;
                return (
                  <tr
                    key={event.id}
                    className="border-t border-[#f3efe6] hover:bg-[#fbfaf6] transition-colors"
                  >
                    <td className="p-0">
                      <Link
                        to={`/events/${event.id}`}
                        className="block px-3 py-2.5 text-[#9a9182] tabular-nums"
                      >
                        {event.date}
                      </Link>
                    </td>
                    <td className="p-0">
                      <Link
                        to={`/events/${event.id}`}
                        className="block px-3 py-2.5 font-medium text-ink hover:text-brand-700"
                      >
                        {event.name}
                      </Link>
                    </td>
                    <td className="p-0">
                      <Link
                        to={`/events/${event.id}`}
                        className="block px-3 py-2.5 text-[#6b6356]"
                      >
                        {leagueName ?? (
                          <span className="italic text-[#9a9182]">
                            sem liga
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="p-0">
                      <Link
                        to={`/events/${event.id}`}
                        className="block px-3 py-2.5 text-[#6b6356]"
                      >
                        {event.format ?? "—"}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-[#6b6356] tabular-nums">
                      {event.meleeEventId ? (
                        <a
                          href={`https://melee.gg/Tournament/View/${event.meleeEventId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-700 hover:underline"
                        >
                          {event.meleeEventId}
                        </a>
                      ) : (
                        <span className="italic text-[#b8b0a0]">—</span>
                      )}
                    </td>
                    <td className="p-0">
                      <Link
                        to={`/events/${event.id}`}
                        className="block px-3 py-2.5 text-right tabular-nums text-[#9a9182]"
                      >
                        {event.id}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => setEditingId(event.id)}
                        className="text-sm font-semibold text-brand-700 hover:underline"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-slate-500 py-8">
                    {search.trim()
                      ? "Nenhum evento corresponde à busca."
                      : "Nenhum evento cadastrado."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          leagues={leaguesQuery.data ?? []}
          onClose={() => setEditingId(null)}
        />
      )}
    </section>
  );
}

interface EditEventModalProps {
  event: Event;
  leagues: League[];
  onClose: () => void;
}

function EditEventModal({ event, leagues, onClose }: EditEventModalProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(event.name);
  const [date, setDate] = useState(event.date);
  const [format, setFormat] = useState(event.format ?? "");
  const [leagueId, setLeagueId] = useState<number | null>(event.leagueId);
  const [meleeEventId, setMeleeEventId] = useState(event.meleeEventId ?? "");
  const [isOpen, setIsOpen] = useState<boolean>(event.isOpen);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sortedLeagues = useMemo(
    () =>
      [...leagues].sort((a, b) =>
        a.startDate !== b.startDate
          ? a.startDate < b.startDate
            ? 1
            : -1
          : a.name.localeCompare(b.name),
      ),
    [leagues],
  );

  const mutation = useMutation({
    mutationFn: (body: AdminEventPatchBody) =>
      api.adminUpdateEvent(event.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event", event.id] });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });
      queryClient.invalidateQueries({ queryKey: ["leagues", "all"] });
      onClose();
    },
    onError: (err: Error) => setErrorMsg(err.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMsg("Nome é obrigatório.");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setErrorMsg("Data inválida (use o seletor).");
      return;
    }
    const body: AdminEventPatchBody = {};
    if (trimmedName !== event.name) body.name = trimmedName;
    if (date !== event.date) body.date = date;
    const formatNext = format.trim() === "" ? null : format.trim();
    if (formatNext !== event.format) body.format = formatNext;
    if (leagueId !== event.leagueId) body.leagueId = leagueId;
    const meleeNext = meleeEventId.trim() === "" ? null : meleeEventId.trim();
    if (meleeNext !== event.meleeEventId) body.meleeEventId = meleeNext;
    if (isOpen !== event.isOpen) body.isOpen = isOpen;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(body);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4 py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-2xl border border-line bg-white shadow-xl space-y-5 p-6"
      >
        <header>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Editar evento · #{event.id}
          </div>
          <h2 className="font-display text-xl font-bold text-ink">
            {event.name}
          </h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nome" className="md:col-span-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </Field>
          <Field label="Data">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </Field>
          <Field label="Formato">
            <input
              type="text"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              placeholder="(opcional)"
              className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </Field>
          <Field label="Liga" className="md:col-span-2">
            <select
              value={leagueId === null ? "" : String(leagueId)}
              onChange={(e) =>
                setLeagueId(e.target.value === "" ? null : Number(e.target.value))
              }
              className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">— sem liga —</option>
              {sortedLeagues.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Melee event ID" className="md:col-span-2">
            <input
              type="text"
              value={meleeEventId}
              onChange={(e) => setMeleeEventId(e.target.value)}
              placeholder="(opcional) ex: 427830"
              inputMode="numeric"
              className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {meleeEventId.trim() && (
              <p className="text-xs text-[#8a8170] mt-1">
                Link:{" "}
                <a
                  href={`https://melee.gg/Tournament/View/${meleeEventId.trim()}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 hover:underline"
                >
                  melee.gg/Tournament/View/{meleeEventId.trim()}
                </a>
              </p>
            )}
          </Field>
          <div className="md:col-span-2">
            <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-line bg-[#faf7ee] px-3 py-2.5">
              <input
                type="checkbox"
                checked={isOpen}
                onChange={(e) => setIsOpen(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-brand-700"
              />
              <span>
                <span className="block text-sm font-semibold text-ink">
                  Marcar como Open
                </span>
                <span className="block text-xs text-[#6b6356] mt-0.5">
                  Aparece na aba pública <strong>Opens</strong>, agrupado por
                  ano. Não afeta ligas nem partidas.
                </span>
              </span>
            </label>
          </div>
        </div>

        {errorMsg && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#f3efe6]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 text-sm font-semibold text-[#6b6356] transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-lg bg-brand-700 hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 text-sm font-semibold transition-colors"
          >
            {mutation.isPending ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={["block text-sm", className].filter(Boolean).join(" ")}>
      <span className="block text-[#6b6356] mb-1 font-medium">{label}</span>
      {children}
    </label>
  );
}
